<template>
  <div
    ref="contentToggle"
    class="content-toggle"
    data-scroll-target
    :class="containerClass"
  >
    <div
      ref="contentToggleContent"
      class="content-toggle__content"
      :style="contentStyle"
    >
      <slot />
    </div>
    <LinkButton
      tag="button"
      type="button"
      class="content-toggle__link"
      text
      @click="isOpen = !isOpen"
    >
      {{ linkText }}
    </LinkButton>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import { LinkButton } from '@i22team/td-longtail-vue-components';

const TEXT_SHOW_LESS = 'Weniger anzeigen';
const TEXT_SHOW_MORE = 'Mehr anzeigen';

export default {
  name: 'ContentToggle',
  components: { LinkButton },
  props: {
    minHeightToCollapse: {
      type: Number,
      default: 200,
    },
  },
  data() {
    return {
      isOpen: false,
      isCollapsable: true, // on ssr we do not know if its needed, so we have to collapse the content to prevent a flickering.
      resizeHandler: debounce(() => {
        if (!this.contentHeight) return;
        if (this.contentHeight > this.minHeightToCollapse) {
          this.isCollapsable = true;
        } else {
          this.isCollapsable = false;
          this.isOpen = false;
        }
      }, 200),
    };
  },
  computed: {
    containerClass() {
      const classes = [];
      if (this.isOpen) classes.push('content-toggle--open');
      if (this.isCollapsable) classes.push('content-toggle--collapsable');
      return classes;
    },
    linkText() {
      return this.isOpen ? TEXT_SHOW_LESS : TEXT_SHOW_MORE;
    },
    contentHeight() {
      const contentEl = this.$refs.contentToggleContent;
      if (!contentEl) return undefined;
      return contentEl.scrollHeight + 24; // 24px for the margin
    },
    contentStyle() {
      if (!this.isCollapsable) return null;
      if (this.isOpen && this.contentHeight) {
        return { 'max-height': `${this.contentHeight}px` };
      } else if (!this.isOpen) {
        return {
          'max-height': `${this.minHeightToCollapse}px`,
        };
      }
      return null;
    },
  },
  watch: {
    isOpen() {
      // Scroll content into view on close.
      if (!this.isOpen) {
        this.$refs.contentToggle?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    },
  },
  mounted() {
    this.resizeHandler();
  },
  created() {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', this.resizeHandler);
  },
  unmounted() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('resize', this.resizeHandler);
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.content-toggle {
  width: 100%;
  margin-bottom: 3 * $grid-base;

  &--collapsable {
    .content-toggle {
      &__content {
        overflow: hidden;

        &:after {
          content: ' ';
          position: absolute;
          pointer-events: none;
          height: 50%;
          left: 0;
          right: 0;
          bottom: 0;
          transition: height 1s ease-in-out;
          background: linear-gradient(180deg, transparent 0%, var(--color-white) 100%);
        }
      }
    }
  }

  &:not(&--collapsable) {
    .content-toggle {
      &__link {
        display: none;
      }
    }
  }

  &--open {
    .content-toggle {
      &__content {
        &:after {
          height: 0;
        }
      }

      &__link {
        &:before {
          content: '–';
          top: 0;
        }
      }
    }
  }

  &__content {
    border-left: 2px solid var(--color-gray-200);
    padding-left: 2 * $grid-base;
    margin-bottom: 2 * $grid-base;
    position: relative;
    transition: max-height 1s ease-in-out;

    // Pretty evil, but there is no other way to intercept HTML coming
    // from Brodos
    :deep(a) {
      color: var(--color-link);

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__link {
    position: relative;
    margin-left: 2 * $grid-base;
    padding-left: $grid-base;

    &:before {
      position: absolute;
      content: '+';
      left: 0;
      top: 2px;
    }
  }
}
</style>
